import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';

import { FinancingFormFooterComponent } from '../financing-form-footer/financing-form-footer.component';
import { FinancingFormProgressComponent } from '../financing-form-progress/financing-form-progress.component';
import { FinancingFormSidebarComponent } from '../financing-form-sidebar/financing-form-sidebar.component';
import { FinancingFormTopComponent } from '../financing-form-top/financing-form-top.component';
import { LoaderComponent } from '@atoms/loader/loader.component';
import { ParagraphComponent } from '@atoms/paragraph/paragraph.component';
import { PerexComponent } from '@atoms/perex/perex.component';
import { FormLayoutComponent } from '@molecules/form-layout/form-layout.component';
import { FormLayoutContentComponent } from '@molecules/form-layout/form-layout-content/form-layout-content.component';
import { FormLayoutFooterComponent } from '@molecules/form-layout/form-layout-footer/form-layout-footer.component';
import { FormLayoutMainComponent } from '@molecules/form-layout/form-layout-main/form-layout-main.component';
import { FormLayoutMiddleComponent } from '@molecules/form-layout/form-layout-middle/form-layout-middle.component';
import { FormLayoutProgressComponent } from '@molecules/form-layout/form-layout-progress/form-layout-progress.component';
import { FormLayoutSidebarComponent } from '@molecules/form-layout/form-layout-sidebar/form-layout-sidebar.component';
import { FormLayoutTitleComponent } from '@molecules/form-layout/form-layout-title/form-layout-title.component';
import { FormLayoutTopComponent } from '@molecules/form-layout/form-layout-top/form-layout-top.component';
import { FinancingCalculatorFormComponent } from '@organisms/financing-calculator/financing-calculator-form/financing-calculator-form.component';
import { CarService } from '@services/car.service';
import { FinancingFormService } from '@services/financing-form.service';
import { GlobalsService } from '@services/globals.service';
import { GtmService } from '@services/gtm.service';
import { LanguageService } from '@services/language.service';
import { SaveFormService } from '@services/save-form.service';
import { ScrolltoService } from '@services/scrollto.service';
import { SsrService } from '@services/ssr.service';
import { CarItem } from '@models/car.model';
import { FinancingCalculator } from '@models/financing-calculator.model';

@Component({
  selector: 'app-financing-form-calculator',
  imports: [
    TranslateModule,
    FinancingFormFooterComponent,
    FinancingFormProgressComponent,
    FinancingFormSidebarComponent,
    FinancingFormTopComponent,
    LoaderComponent,
    ParagraphComponent,
    PerexComponent,
    FormLayoutComponent,
    FormLayoutContentComponent,
    FormLayoutFooterComponent,
    FormLayoutMainComponent,
    FormLayoutMiddleComponent,
    FormLayoutProgressComponent,
    FormLayoutSidebarComponent,
    FormLayoutTitleComponent,
    FormLayoutTopComponent,
    FinancingCalculatorFormComponent,
  ],
  templateUrl: './financing-form-calculator.component.html',
  styleUrl: './financing-form-calculator.component.scss',
})
export class FinancingFormCalculatorComponent implements OnInit {
  private cd = inject(ChangeDetectorRef);
  private router = inject(Router);
  private location = inject(Location);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private carService = inject(CarService);
  public financingFormService = inject(FinancingFormService);
  public globalsService = inject(GlobalsService);
  private gtmService = inject(GtmService);
  private languageService = inject(LanguageService);
  private saveFormService = inject(SaveFormService);
  private scrolltoService = inject(ScrolltoService);
  public ssrService = inject(SsrService);
  public carId = this.activatedRoute.snapshot.queryParamMap.get('car') || '';
  public car: WritableSignal<CarItem | undefined> = signal(undefined);
  public loanType: string = '1';
  public months?: number;
  public advance?: number;
  public loaded: WritableSignal<boolean> = signal(false);

  calculatorChanged(event: FinancingCalculator): void {
    this.months = event.months;
    this.advance = event.advance;
    this.cd.detectChanges();
  }

  saveData(): void {
    this.saveFormService.editForm(this.financingFormService.formId());
  }

  getCar(): void {
    this.carService
      .getCarData(Number(this.carId))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.car.set(data);
          this.loaded.set(true);
        },
      });
  }

  constructor() {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.loaded.set(false);
        const hash = this.location.path(true).split('#')[1];
        const fragmentParams = this.globalsService.getFragmentParams(hash);
        this.loanType = fragmentParams['loan-type'] || '1';
        this.months = Number(fragmentParams['months'] ?? 60);
        this.advance = Number(fragmentParams['advance'] ?? 0);
        this.getCar();
      }
    });
  }

  ngOnInit(): void {
    this.scrolltoService.scrollTo('top', 'instant');
    this.financingFormService.step.set(1);

    if (this.financingFormService.financingFormData()?.carId !== +this.carId) this.financingFormService.resetData();
    this.getCar();

    const dataLayer = {
      event: 'page_view',
      pageURL: '/' + this.languageService.language() + '/mam-zajem-o-financovani',
      pageType: 'Form-1',
      pageTitle: 'Kalkulačka',
      data: {
        splatka: this.financingFormService.product()?.monthlyPayment,
        akontace: this.financingFormService.carContractData()?.advance,
        kategorie_zakazniku: this.loanType,
        doba_splaceni: this.financingFormService.carContractData()?.numberOfMonths,
      },
    };
    this.gtmService.pushGtm(dataLayer);
  }
}
